import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { SelectionModel } from '@angular/cdk/collections';
import { DOWN_ARROW, END, ENTER, ESCAPE, HOME, SPACE, TAB, UP_ARROW } from '@angular/cdk/keycodes';
import { ConnectionPositionPair, Overlay, OverlayRef, PositionStrategy, ViewportRuler } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  AfterContentInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, ContentChildren, ElementRef,
  EventEmitter, HostBinding, HostListener, Input, OnDestroy, OnInit, Optional, Output, QueryList, Renderer2, Self,
  TemplateRef, ViewChild, ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, NgControl, UntypedFormControl } from '@angular/forms';

import { fromEvent, merge, Observable, Subject } from 'rxjs';
import { debounceTime, filter, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

import { MdbAbstractFormControl } from '../forms/index';
import { MDB_OPTION_PARENT, MdbOptionComponent, MdbOptionGroupComponent } from '../option/index';
import { MdbSelectAllOptionComponent } from './select-all-option';
import { dropdownAnimation, dropdownContainerAnimation } from './select-animations';

export type MdbSelectFilterFn = (option: string, filterValue: string) => boolean;

@Component({
  selector: 'mdb-select',
  templateUrl: './select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [dropdownAnimation, dropdownContainerAnimation],
  providers: [
    { provide: MdbAbstractFormControl, useExisting: MdbSelectComponent },
    { provide: MDB_OPTION_PARENT, useExisting: MdbSelectComponent },
  ],
  standalone: false,
})

// eslint-disable-next-line @angular-eslint/component-class-suffix
export class MdbSelectComponent
  implements OnInit, OnDestroy, AfterContentInit, ControlValueAccessor
{
  @ViewChild('input', { static: true }) _input: ElementRef<HTMLInputElement>;
  @ViewChild('dropdownTemplate') _dropdownTemplate: TemplateRef<any>;
  @ViewChild('dropdown') dropdown: ElementRef;
  @ViewChild('optionsWrapper') private _optionsWrapper: ElementRef;
  @ViewChild('customContent') _customContent: ElementRef;
  @ViewChild('filterInput', { static: false }) _filterInput: ElementRef;
  @ContentChild(MdbSelectAllOptionComponent) selectAllOption: MdbSelectAllOptionComponent;
  @ContentChildren(MdbOptionComponent, { descendants: true })
  options: QueryList<MdbOptionComponent>;
  @ContentChildren(MdbOptionGroupComponent) optionGroups: QueryList<MdbOptionGroupComponent>;

  @Input()
  get autoSelect(): boolean {
    return this._autoSelect;
  }
  set autoSelect(value: boolean) {
    this._autoSelect = coerceBooleanProperty(value);
  }
  private _autoSelect = false;

  @Input()
  get clearButton(): boolean {
    return this._clearButton;
  }
  set clearButton(value: boolean) {
    this._clearButton = coerceBooleanProperty(value);
  }
  private _clearButton = false;

  @Input() clearButtonTabindex = 0;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
  }
  private _disabled = false;

  @Input() dropdownClass: string;
  @Input() displayedLabels = 10;

  @Input()
  get highlightFirst(): boolean {
    return this._highlightFirst;
  }
  set highlightFirst(value: boolean) {
    this._highlightFirst = coerceBooleanProperty(value);
  }
  private _highlightFirst = true;

  @Input()
  get multiple(): boolean {
    return this._multiple;
  }
  set multiple(value: boolean) {
    this._multiple = coerceBooleanProperty(value);
  }
  private _multiple = false;

  @Input() notFoundMsg = 'No results found';

  @Input() optionsSelectedLabel = 'options selected';
  @Input() placeholder = '';

  @Input()
  get tabindex(): number {
    return this._tabindex;
  }
  set tabindex(value: number) {
    this._tabindex = coerceNumberProperty(value);
  }
  private _tabindex = 0;

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
  }
  private _required = false;

  @Input()
  get filter(): boolean {
    return this._filter;
  }
  set filter(value: boolean) {
    this._filter = coerceBooleanProperty(value);
  }
  private _filter = false;

  @Input() filterFn: MdbSelectFilterFn = (option, filterValue) => option.includes(filterValue);

  @Input() filterPlaceholder = 'Search...';
  @Input() filterDebounce = 300;
  @Input('aria-label') ariaLabel: string;
  @Input('aria-labelledby') ariaLabelledby: string;

  @Input()
  get visibleOptions(): number {
    return this._visibleOptions;
  }

  set visibleOptions(value: number) {
    if (value !== 0) {
      this._visibleOptions = value;
      this.dropdownHeight = this.visibleOptions * this.optionHeight;
    }
  }
  private _visibleOptions = 10;

  @Input()
  get optionHeight(): any {
    return this._optionHeight;
  }

  set optionHeight(value: any) {
    if (value !== 0) {
      this._optionHeight = value;
      this.dropdownHeight = this.visibleOptions * this.optionHeight;
    }
  }

  private _optionHeight = 35;

  @Input()
  get dropdownHeight(): number {
    return this._dropdownHeight;
  }

  set dropdownHeight(value: number) {
    if (value !== 0) {
      this._dropdownHeight = value;
    }
  }
  protected _dropdownHeight = this.visibleOptions * this.optionHeight;

  @Input()
  get value(): any {
    return this._value;
  }
  set value(newValue: any) {
    if (newValue !== this._value) {
      if (this.options) {
        this._setSelection(newValue);
      }

      this._value = newValue;
    }
  }
  private _value: any;

  @Input()
  // eslint-disable-next-line
  get compareWith() {
    return this._compareWith;
  }
  set compareWith(fn: (o1: any, o2: any) => boolean) {
    if (typeof fn === 'function') {
      this._compareWith = fn;
    }
  }

  @Input() sortComparator: (
    a: MdbOptionComponent,
    b: MdbOptionComponent,
    options: MdbOptionComponent[]
  ) => number;

  @Input() size: 'sm' | 'lg' | 'default' = 'default';
  @Input() inputId: string;
  @Input() inputFilterId: string;

  @Output() readonly valueChange: EventEmitter<any> = new EventEmitter<any>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() readonly change: EventEmitter<any> = new EventEmitter<any>();
  @Output() opened: EventEmitter<void> = new EventEmitter<void>();
  @Output() closed: EventEmitter<void> = new EventEmitter<void>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() readonly search = new EventEmitter<string>();
  @Output() selected: EventEmitter<MdbOptionComponent> = new EventEmitter<MdbOptionComponent>();
  @Output() deselected: EventEmitter<MdbOptionComponent | MdbOptionComponent[]> = new EventEmitter<
    MdbOptionComponent | MdbOptionComponent[]
  >();

  readonly stateChanges: Subject<void> = new Subject<void>();

  selectFilter: UntypedFormControl = new UntypedFormControl();

  get activeOption(): MdbOptionComponent | null {
    if (this._keyManager) {
      return this._keyManager.activeItem;
    }

    return null;
  }

  get selectionView(): string {
    if (
      this.multiple &&
      this.displayedLabels !== -1 &&
      this._selectionModel.selected.length > this.displayedLabels
    ) {
      return `${this._selectionModel.selected.length} ${this.optionsSelectedLabel}`;
    }

    if (this.multiple) {
      const selectedOptions = this._selectionModel.selected.map((option) => option.label.trim());

      return selectedOptions.join(', ');
    }

    if (this._selectionModel.selected[0]) {
      return (this._selectionModel.selected[0].label ?? "").trim();
    }

    return '';
  }

  get hasSelection(): boolean {
    return this._selectionModel && !this._selectionModel.isEmpty();
  }

  get allChecked(): boolean {
    const selectionsNumber = this._selectionModel.selected.length;
    const optionsNumber = this.options.length;

    return selectionsNumber === optionsNumber;
  }

  private _keyManager: ActiveDescendantKeyManager<MdbOptionComponent | null>;

  private _overlayRef: OverlayRef | null;
  private _portal: TemplatePortal;

  private _selectionModel: SelectionModel<MdbOptionComponent>;

  previousSelectedValues: any;

  private _destroy = new Subject<void>();

  _isOpen = false;

  _hasFocus = false;

  _labelActive = false;

  _showNoResultsMsg = false;

  private _selectAllChecked = false;

  private _compareWith = (o1: any, o2: any) => o1 === o2;

  @HostListener('click', ['$event'])
  handleClick(event: MouseEvent): void {
    if (!this.disabled) {
      this.toggle();
    }
  }

  @HostListener('keydown', ['$event'])
  handleKeydown(event: any): void {
    if (!this.disabled) {
      this._handleClosedKeydown(event);
    }
  }

  @HostBinding('class.select')
  get select(): boolean {
    return true;
  }

  @HostBinding('class.d-block')
  get display(): boolean {
    return true;
  }

  @HostBinding('class.focused')
  get isFocused(): boolean {
    return this._hasFocus || this._isOpen;
  }

  @HostBinding('class.active')
  get isActive(): boolean {
    return this.hasSelected || this.isFocused;
  }

  @HostBinding('attr.aria-multiselectable')
  get isMultiselectable(): boolean {
    return this.multiple;
  }

  @HostBinding('attr.aria-haspopup')
  get hasPopup(): boolean {
    return true;
  }

  @HostBinding('attr.aria-disabled')
  get isDisabled(): boolean {
    return this.disabled;
  }

  @HostBinding('attr.aria-expanded')
  get isExpanded(): boolean {
    return this._isOpen;
  }

  @HostBinding('attr.aria-role')
  get role(): string {
    return this.filter ? 'combobox' : 'listbox';
  }

  @HostBinding('class')
  get sizeClass(): string {
    return this.size === 'lg' ? 'select-lg' : this.size === 'sm' ? 'select-sm' : '';
  }

  @HostBinding('attr.aria-label')
  get ariaLabelValue(): string {
    return this.ariaLabel;
  }

  @HostBinding('attr.aria-labelledby')
  get ariaLabelledbyValue(): string {
    return this.ariaLabelledby;
  }

  constructor(
    private _overlay: Overlay,
    private _viewportRuler: ViewportRuler,
    private _vcr: ViewContainerRef,
    private _cdRef: ChangeDetectorRef,
    private _renderer: Renderer2,
    private _elementRef: ElementRef,
    @Self() @Optional() public ngControl: NgControl
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngAfterContentInit(): void {
    this._initKeyManager(this.options);
    this._setInitialValue();
    this._listenToOptionClick();

    if (this.selectAllOption) {
      this._listenToSelectAllClick();
    }

    this._listenToSelectedLabelChange();
  }

  private _initKeyManager(viewOptions): void {
    const options = this.selectAllOption ? [this.selectAllOption, ...viewOptions] : viewOptions;

    if (this.filter) {
      this._keyManager = new ActiveDescendantKeyManager<MdbOptionComponent | null>(
        options
      ).withVerticalOrientation();
    } else {
      this._keyManager = new ActiveDescendantKeyManager<MdbOptionComponent | null>(options)
        .withTypeAhead(200)
        .withVerticalOrientation();
    }
  }

  private _listenToOptionClick(): void {
    this.options.changes
      .pipe(
        startWith(this.options),
        tap(() => {
          this._setInitialValue();
          setTimeout(() => {
            this._showNoResultsMsg = this.options.length === 0;
            this._keyManager.setActiveItem(null);
            this._initKeyManager(this.options);

            if (this._isOpen) {
              this._highlightFirstOption();

              if (this._keyManager.activeItem) {
                this._scrollToOption(this._keyManager.activeItem);
              }
            }
          }, 0);
        }),
        switchMap((options: QueryList<MdbOptionComponent>) => {
          return merge(...options.map((option: MdbOptionComponent) => option.click$));
        }),
        takeUntil(this._destroy)
      )
      .subscribe((clickedOption: MdbOptionComponent) => this._handleOptionClick(clickedOption));
  }

  private _listenToSelectAllClick(): void {
    this.selectAllOption.click$
      .pipe(takeUntil(this._destroy))
      .subscribe((option: MdbSelectAllOptionComponent) => {
        this.onSelectAll(option);
      });
  }

  private _listenToSelectedLabelChange(): void {
    this.options.changes.pipe(startWith(null), takeUntil(this._destroy)).subscribe(() => {
      merge(...this.options.map((option) => option._labelChange))
        .pipe(takeUntil(merge(this._destroy, this.options.changes)))
        .subscribe(() => {
          this.stateChanges.next();
          this._cdRef.markForCheck();
        });
    });
  }

  private _updateValue(): void {
    let updatedValue: any = null;

    if (this.multiple) {
      updatedValue = this._selectionModel.selected.map((option) => option.value);
    } else {
      updatedValue = this._selectionModel.selected[0].value;
    }

    this._value = updatedValue;
    this._cdRef.markForCheck();
  }

  private _emitLegacyChange(value: any): void {
    this.change.emit({ target: { value }, value });
  }

  private _handleOptionClick(option: MdbOptionComponent): void {
    if (option.disabled) {
      return;
    }

    if (this.multiple) {
      this._handleMultipleSelection(option);
    } else {
      this._handleSingleSelection(option);
    }

    this.stateChanges.next();
    this._cdRef.markForCheck();
  }

  private _handleSingleSelection(option: MdbOptionComponent): void {
    const currentSelection = this._selectionModel.selected[0];

    this._selectionModel.select(option);
    option.select();

    if (currentSelection && currentSelection !== option) {
      this._selectionModel.deselect(currentSelection);
      currentSelection.deselect();
      this.deselected.emit(currentSelection.value);
    }

    if (!currentSelection || (currentSelection && currentSelection !== option)) {
      this._updateValue();
      this.valueChange.emit(this.value);
      this._emitLegacyChange(this.value);
      this._onChange(this.value);
      this.selected.emit(option.value);
    }

    this.close();
    this._focus();
  }

  private _handleMultipleSelection(option: MdbOptionComponent): void {
    if (option.selected) {
      this._selectionModel.deselect(option);
      option.deselect();
      this.deselected.emit(option.value);
    } else {
      this._selectionModel.select(option);
      option.select();
      this.selected.emit(option.value);
    }

    this._selectAllChecked = this.allChecked ? true : false;

    if (this.selectAllOption && !this._selectAllChecked) {
      this.selectAllOption.deselect();
    } else if (this.selectAllOption && this._selectAllChecked) {
      this.selectAllOption.select();
    }

    this._updateValue();
    this._sortValues();
    this.valueChange.emit(this.value);
    this._emitLegacyChange(this.value);
    this._onChange(this.value);
    this._cdRef.markForCheck();
  }

  private _setSelection(selectValue: any | any[]): void {
    const previousSelected = this._selectionModel.selected;

    previousSelected.forEach((selectedOption: MdbOptionComponent) => {
      selectedOption.deselect();
    });
    this._selectionModel.clear();

    if (selectValue != null && Array.isArray(selectValue) && selectValue.length) {
      selectValue?.forEach((value: any) => this._selectByValue(value));
      this._sortValues();
    } else {
      this._selectByValue(selectValue);
    }


    if (this.selectAllOption) {
      if (this.allChecked) {
        this.selectAllOption.select();
        this._selectAllChecked = true;
      } else {
        this.selectAllOption.deselect();
        this._selectAllChecked = false;
      }
    }

    this.stateChanges.next();
    this._cdRef.markForCheck();
  }

  private _showFilteredOptions(): void {
    this.options.toArray().forEach((option: MdbOptionComponent) => {
      option.hidden = false;
    });

    this.optionGroups.toArray().forEach((group: MdbOptionGroupComponent) => {
      group.hidden = false;
    });

    this._showNoResultsMsg = false;
  }

  private _selectByValue(value: any): void {
    const matchingOption = this.options
      .toArray()
      .find(
        (option: MdbOptionComponent) =>
          option.value != null && this._compareWith(option.value, value)
      );

    if (matchingOption) {
      this._selectionModel.select(matchingOption);
      matchingOption.select();
    }
  }

  private _setInitialValue(): void {
    Promise.resolve().then(() => {
      const value = this.ngControl ? this.ngControl.value : this._value;
      this._setSelection(value);
    });
  }

  onSelectAll(selectAlloption: MdbSelectAllOptionComponent): void {
    if (selectAlloption.disabled) {
      return;
    }

    if (!selectAlloption.selected && !this._selectAllChecked) {
      this._selectAllChecked = true;
      this.options.forEach((option: MdbOptionComponent) => {
        if (!option.disabled && !option.hidden) {
          this._selectionModel.select(option);
          option.select();
        }
      });
      this._updateValue();
      this._sortValues();
      this.valueChange.emit(this.value);
      this._emitLegacyChange(this.value);
      this._onChange(this.value);
      selectAlloption.select();
    } else {
      this._selectAllChecked = false;
      this.options.forEach((option: MdbOptionComponent) => {
        if (!option.hidden) {
          option.deselect();
          this._selectionModel.deselect(option);
        }
      });
      selectAlloption.deselect();
      this._updateValue();
      this.valueChange.emit(this.value);
      this._emitLegacyChange(this.value);
      this._onChange(this.value);
    }
  }

  open(): void {
    if (this.disabled) {
      return;
    }

    let overlayRef = this._overlayRef;

    if (!overlayRef) {
      this._portal = new TemplatePortal(this._dropdownTemplate, this._vcr);

      overlayRef = this._overlay.create({
        width: this.input.offsetWidth,
        scrollStrategy: this._overlay.scrollStrategies.reposition(),
        positionStrategy: this._getOverlayPosition(),
      });

      this._overlayRef = overlayRef;
    }

    if (this.filter) {
      this._showFilteredOptions();
      this.selectFilter.setValue('');
      this._initKeyManager(this.options);
    }

    if (overlayRef && !overlayRef.hasAttached()) {
      overlayRef.attach(this._portal);
      this._listenToOutSideClick(overlayRef, this.input).subscribe(() => this.close());

      this._highlightFirstOption();
    }

    this._cdRef.detectChanges();

    if (this._viewportRuler) {
      this._viewportRuler
        .change()
        .pipe(takeUntil(this._destroy))
        .subscribe(() => {
          if (this._isOpen && overlayRef) {
            overlayRef.updateSize({ width: this.input.offsetWidth });
          }
        });
    }

    setTimeout(() => {
      if (this.filter) {
        this._filterInput.nativeElement.focus();

        this.selectFilter.valueChanges
          .pipe(debounceTime(this.filterDebounce), takeUntil(this.closed))
          .subscribe((value: string) => {
            this.search.emit(value);

            const filterValue = value.toLowerCase();

            const options = this.options.toArray().filter((option: MdbOptionComponent) => {
              if (this.filterFn(option.label.toLowerCase().trim(), filterValue)) {
                option.hidden = false;
                return option;
              } else {
                option.hidden = true;
                return false;
              }
            });

            this.optionGroups.toArray().filter((group: MdbOptionGroupComponent) => {
              if (options.some((option: MdbOptionComponent) => option.group === group)) {
                group.hidden = false;
                return group;
              } else {
                group.hidden = true;
                return false;
              }
            });

            this._selectAllChecked = options.every((option) => option.selected);

            if (this.selectAllOption && !this._selectAllChecked) {
              this.selectAllOption.deselect();
            } else if (this.selectAllOption && this._selectAllChecked) {
              this.selectAllOption.select();
            }

            this._showNoResultsMsg = options.length === 0;
            this._keyManager.setActiveItem(null);
            this._initKeyManager(options);
            if (filterValue.length === 0) {
              this._highlightFirstOption();
            }
          });
      }
    }, 0);

    setTimeout(() => {
      const firstSelected = this._selectionModel.selected[0];
      if (firstSelected) {
        this._scrollToOption(firstSelected);
      }
    }, 0);

    this.opened.emit();

    setTimeout(() => {
      this._renderer.listen(this.dropdown.nativeElement, 'keydown', (event: KeyboardEvent) => {
        this._handleOpenKeydown(event);
      });
    }, 0);

    if (!this.filter) {
      setTimeout(() => {
        this.dropdown.nativeElement.focus();
      }, 0);
    }

    this._isOpen = true;
    this._cdRef.markForCheck();
  }

  private _sortValues(): void {
    if (this.multiple) {
      const options = this.options.toArray();

      this._selectionModel.sort((a, b) => {
        return this.sortComparator
          ? this.sortComparator(a, b, options)
          : options.indexOf(a) - options.indexOf(b);
      });
    }
  }

  private _listenToOutSideClick(
    overlayRef: OverlayRef,
    origin: HTMLElement
  ): Observable<MouseEvent> {
    return fromEvent<MouseEvent>(document, 'click').pipe(
      filter((event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const hostElement = this._elementRef.nativeElement as HTMLElement;
        const notHost = !hostElement.contains(target);
        // const notValue = !this._selectValue.nativeElement.contains(target);
        const notOverlay = !!overlayRef && overlayRef.overlayElement.contains(target) === false;
        return notHost && notOverlay;
      }),
      takeUntil(overlayRef.detachments())
    );
  }

  private _getOverlayPosition(): PositionStrategy {
    const positionStrategy = this._overlay
      .position()
      .flexibleConnectedTo(this.input)
      .withPositions(this._getPositions())
      .withFlexibleDimensions(false)
      .withPush(false);

    return positionStrategy;
  }

  private _getPositions(): ConnectionPositionPair[] {
    // If labeQl floats we need to add additional offset for top position
    // Bottom offset is needed because of the box-shadow on input border
    const bottomOffset = 1;
    const topOffset = -6;

    return [
      {
        originX: 'start',
        originY: 'bottom',
        offsetY: bottomOffset,
        overlayX: 'start',
        overlayY: 'top',
      },
      {
        originX: 'start',
        originY: 'top',
        offsetY: topOffset,
        overlayX: 'start',
        overlayY: 'bottom',
      },
    ];
  }

  close(): void {
    if (!this._isOpen) {
      return;
    }

    if (this._overlayRef && this._overlayRef.hasAttached()) {
      this._overlayRef.detach();
      this._isOpen = false;
    }

    this.closed.emit();
    this._keyManager.setActiveItem(null);
    this._onTouched();
    this._cdRef.markForCheck();
  }

  toggle(): void {
    this._isOpen ? this.close() : this.open();
  }

  get hasSelected(): boolean {
    return this._selectionModel.selected.length !== 0;
  }

  get input(): HTMLInputElement {
    return this._input.nativeElement;
  }

  get labelActive(): boolean {
    return this.hasSelected;
  }

  private _scrollToOption(option: MdbOptionComponent): void {
    let optionIndex: number;

    if (this.multiple && this.selectAllOption) {
      optionIndex = this.options.toArray().indexOf(option) + 1;
    } else {
      optionIndex = this.options.toArray().indexOf(option);
    }

    const groupsNumber = this._getNumberOfGroupsBeforeOption(optionIndex);

    const scrollToIndex = optionIndex + groupsNumber;

    const list = this._optionsWrapper.nativeElement;
    const listHeight = list.offsetHeight;

    if (optionIndex > -1) {
      const optionTop = scrollToIndex * this.optionHeight;
      const optionBottom = optionTop + this.optionHeight;

      const viewTop = list.scrollTop;
      const viewBottom = this.dropdownHeight;

      if (optionBottom > viewBottom) {
        list.scrollTop = optionBottom - listHeight;
      } else if (optionTop < viewTop) {
        list.scrollTop = optionTop;
      }
    }
  }

  private _getNumberOfGroupsBeforeOption(optionIndex: number): number {
    if (this.optionGroups.length) {
      const optionsList = this.options.toArray();
      const groupsList = this.optionGroups.toArray();
      const index = this.multiple ? optionIndex - 1 : optionIndex;
      let groupsNumber = 0;

      for (let i = 0; i <= index; i++) {
        if (optionsList[i].group && optionsList[i].group === groupsList[groupsNumber]) {
          groupsNumber++;
        }
      }

      return groupsNumber;
    }

    return 0;
  }

  handleSelectionClear(event?: MouseEvent): void {
    if (this.disabled || (event && event.button === 2)) {
      return;
    }

    this._selectionModel.clear();
    this.options.forEach((option: MdbOptionComponent) => {
      option.deselect();
    });

    if (this.selectAllOption && this._selectAllChecked) {
      this.selectAllOption.deselect();
      this._selectAllChecked = false;
    }
    this.value = null;
    this.valueChange.emit(null);
    this._emitLegacyChange(null);
    this._onChange(null);
    this._selectAllChecked = false;
  }

  private _handleOpenKeydown(event: any): void {
    const key = event.keyCode;
    const manager = this._keyManager;
    const isUserTyping = manager.isTyping();
    const previousActiveItem = manager.activeItem;
    manager.onKeydown(event);

    if (key === TAB || (key === TAB && event.shiftKey)) {
      if (this.autoSelect && !this.multiple && manager.activeItem) {
        this._handleOptionClick(manager.activeItem);
      }

      this._focus();
      this.close();
    }

    if (key === HOME || key === END) {
      event.preventDefault();
      key === HOME ? manager.setFirstItemActive() : manager.setLastItemActive();
      if (manager.activeItem) {
        this._scrollToOption(manager.activeItem);
      }
    } else if (
      this._overlayRef &&
      this._overlayRef.hasAttached() &&
      !isUserTyping &&
      manager.activeItem &&
      (key === ENTER || (key === SPACE && !this.filter))
    ) {
      event.preventDefault();

      if (this.multiple && this.selectAllOption && manager.activeItemIndex === 0) {
        this.onSelectAll(this.selectAllOption);
      } else {
        this._handleOptionClick(manager.activeItem);
      }
    } else if (((key === UP_ARROW || key === DOWN_ARROW) && event.altKey) || key === ESCAPE) {
      event.preventDefault();
      this.close();
      this._focus();
    } else if (key === UP_ARROW || key === DOWN_ARROW) {
      if (manager.activeItem && manager.activeItem !== previousActiveItem) {
        this._scrollToOption(manager.activeItem);
      }
    }
  }

  private _handleClosedKeydown(event: any): void {
    const key = event.keyCode;
    const manager = this._keyManager;

    if (
      ((key === DOWN_ARROW || key === UP_ARROW) && event.altKey) ||
      key === ENTER ||
      key === SPACE
    ) {
      event.preventDefault();
      this.open();
    } else if (!this.multiple && key === DOWN_ARROW) {
      event.preventDefault();
      manager.setNextItemActive();
      if (manager.activeItem) {
        this._handleOptionClick(manager.activeItem);
      }
    } else if (!this.multiple && key === UP_ARROW) {
      event.preventDefault();
      manager.setPreviousItemActive();
      if (manager.activeItem) {
        this._handleOptionClick(manager.activeItem);
      }
    } else if (!this.multiple && key === HOME) {
      event.preventDefault();
      manager.setFirstItemActive();
      if (manager.activeItem) {
        this._handleOptionClick(manager.activeItem);
      }
    } else if (!this.multiple && key === END) {
      event.preventDefault();
      manager.setLastItemActive();
      if (manager.activeItem) {
        this._handleOptionClick(manager.activeItem);
      }
    } else if (this.multiple && (key === DOWN_ARROW || key === UP_ARROW)) {
      event.preventDefault();
      this.open();
    }
  }

  handleOptionsWheel(event: any): void {
    const optionsList = this._optionsWrapper.nativeElement;
    const atTop = optionsList.scrollTop === 0;
    const atBottom = optionsList.offsetHeight + optionsList.scrollTop === optionsList.scrollHeight;

    if (atTop && event.deltaY < 0) {
      event.preventDefault();
    } else if (atBottom && event.deltaY > 0) {
      event.preventDefault();
    }
  }

  private _focus(): void {
    this._hasFocus = true;
    this.input.focus();
  }

  private _highlightFirstOption(): void {
    if (!this.highlightFirst) return;
    if (!this.hasSelection) {
      this._keyManager.setFirstItemActive();
    } else if (this.hasSelection && !this._selectionModel.selected[0].disabled) {
      this._keyManager.setActiveItem(this._selectionModel.selected[0]);
    }
  }

  onFocus(): void {
    if (!this.disabled) {
      this._focus();
      this.stateChanges.next();
    }
  }

  onBlur(): void {
    if (!this._isOpen && !this.disabled) {
      this._onTouched();
    }
    this._hasFocus = false;
    this.stateChanges.next();
  }

  ngOnInit(): void {
    this._selectionModel = new SelectionModel<MdbOptionComponent>(this.multiple);
  }

  ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
    this._destroyDropdown();
  }

  private _destroyDropdown() {
    if (this._overlayRef) {
      this.close();
      this._overlayRef.dispose();
      this._overlayRef = null;
    }
  }

  // ControlValueAccessor interface methods.

  private _onChange = (_: any) => {};
  private _onTouched = () => {};

  writeValue(value: any): void {
    this.value = value;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._cdRef.markForCheck();
  }

  registerOnChange(fn: (_: any) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  static ngAcceptInputType_clearButton: BooleanInput;
  static ngAcceptInputType_disabled: BooleanInput;
  static ngAcceptInputType_highlightFirst: BooleanInput;
  static ngAcceptInputType_multiple: BooleanInput;
  static ngAcceptInputType_required: BooleanInput;
  static ngAcceptInputType_filter: BooleanInput;
}
